import React, { useState, useRef, useEffect } from 'react';
import { CalendarIcon, XIcon } from 'lucide-react';
import { theme } from '../config/theme';
import { getBlockedDates } from '../services/api';

interface DateRangePickerProps {
  startDate: string;
  endDate?: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange?: (date: string) => void;
  onClearDates?: () => void;
  minDate?: string;
  maxDays?: number;
  availabilityMap?: { [date: string]: boolean };
  productId?: string;
  singleDate?: boolean;
  className?: string;
  preserveOpenState?: boolean;
  disableToday?: boolean;
}

interface BlockedDate {
  id: string;
  date: string;
  reason: string;
  created_at: string;
}

const getToday = (disableToday = false) => {
  const today = new Date();
  if (disableToday) {
    // Return tomorrow's date when today should be disabled
    today.setDate(today.getDate() + 1);
  }
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate = '',
  onStartDateChange,
  onEndDateChange,
  onClearDates,
  minDate,
  maxDays = 7,
  availabilityMap,
  productId,
  singleDate = false,
  className = '',
  preserveOpenState = false,
  disableToday = false
}) => {
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(getToday(disableToday));
  const [tempEndDate, setTempEndDate] = useState(startDate || getToday(disableToday));
  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
  
  // Store previous open state to preserve during re-renders
  const [wasStartPickerOpen, setWasStartPickerOpen] = useState(false);
  const [wasEndPickerOpen, setWasEndPickerOpen] = useState(false);

  // Preserve open state when preserveOpenState is true
  useEffect(() => {
    if (preserveOpenState) {
      setWasStartPickerOpen(showStartPicker);
      setWasEndPickerOpen(showEndPicker);
    }
  }, [showStartPicker, showEndPicker, preserveOpenState]);
  
  // Restore open state on component mount/update
  useEffect(() => {
    if (preserveOpenState && (wasStartPickerOpen || wasEndPickerOpen)) {
      setShowStartPicker(wasStartPickerOpen);
      setShowEndPicker(wasEndPickerOpen);
    }
  }, [preserveOpenState, wasStartPickerOpen, wasEndPickerOpen]);
  
  const startPickerRef = useRef<HTMLDivElement>(null);
  const endPickerRef = useRef<HTMLDivElement>(null);

  // Close pickers when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (startPickerRef.current && !startPickerRef.current.contains(event.target as Node)) {
        setShowStartPicker(false);
      }
      if (endPickerRef.current && !endPickerRef.current.contains(event.target as Node)) {
        setShowEndPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update temp dates when props change
  React.useEffect(() => {
    // Show selected date's month if available, otherwise today
    if (showStartPicker) {
      setTempStartDate(startDate || getToday(disableToday));
    }
  }, [showStartPicker, startDate, disableToday]);

  // Sync temp start date when startDate prop changes
  React.useEffect(() => {
    if (startDate) {
      setTempStartDate(startDate);
    }
  }, [startDate]);

  React.useEffect(() => {
    // When end picker opens, show the month of the selected end date or start date
    if (showEndPicker) {
      setTempEndDate(endDate || startDate || getToday(disableToday));
    }
  }, [showEndPicker, startDate, endDate, disableToday]);

  // Sync temp end date when endDate prop changes
  React.useEffect(() => {
    if (endDate) {
      setTempEndDate(endDate);
    }
  }, [endDate]);

  const getMaxDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30); // 30 days from now
    return date.toISOString().split('T')[0];
  };

  const generateCalendarDays = (currentDate: Date, isEndPicker: boolean = false) => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDateInMonth = firstDay.getDay();
    const endDateInMonth = lastDay.getDate();

    const days = [];
    
    // Empty cells for days before month starts
    for (let i = 0; i < startDateInMonth; i++) {
      days.push(<div key={`empty-${i}`} className="p-2"></div>);
    }

    // Days of the month
    for (let day = 1; day <= endDateInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isToday = dateStr === getToday();
      
      // Compare dates at midnight local time to avoid timezone issues
      const todayDate = new Date();
      todayDate.setHours(0, 0, 0, 0);
      const compareDate = new Date(year, month, day);
      compareDate.setHours(0, 0, 0, 0);
      
      const isPast = compareDate < todayDate;
      const isMinDate = minDate ? (() => {
        const minDateParts = minDate.split('-');
        const minDateObj = new Date(parseInt(minDateParts[0]), parseInt(minDateParts[1]) - 1, parseInt(minDateParts[2]));
        minDateObj.setHours(0, 0, 0, 0);
        return compareDate < minDateObj;
      })() : false;
      
      // Check if date is blocked (use timezone-agnostic comparison)
      const isBlocked = blockedDates.some(blocked => {
        return blocked.date === dateStr;
      });
      
      // Check availability for this date
      const isAvailable = availabilityMap ? availabilityMap[dateStr] !== false : true;
      
      // Calculate disabled state
      let isDisabled = isPast || isMinDate || isBlocked;
      if (disableToday && isToday) {
        isDisabled = true;
      }
      if (availabilityMap && !isAvailable) {
        isDisabled = true;
      }
      
      let isSelected = false;
      let isInRange = false;
      let isStartDateInEndPicker = false;

      // Check if this date is the selected start or end date
      if (!isEndPicker) {
        isSelected = dateStr === startDate;
      } else {
        isSelected = dateStr === endDate;
        // Track if this is the start date shown in the end picker
        isStartDateInEndPicker = dateStr === startDate;
      }

      if (isEndPicker && startDate) {
        const startParts = startDate.split('-');
        const start = new Date(parseInt(startParts[0]), parseInt(startParts[1]) - 1, parseInt(startParts[2]));
        start.setHours(0, 0, 0, 0);
        
        const end = new Date(year, month, day);
        end.setHours(0, 0, 0, 0);
        
        const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysDiff < 0) {
          isDisabled = true;
        } else {
          // Highlight both start date and end date in the end picker
          isSelected = dateStr === endDate || dateStr === startDate;
          if (endDate) {
            const endParts = endDate.split('-');
            const endDateObj = new Date(parseInt(endParts[0]), parseInt(endParts[1]) - 1, parseInt(endParts[2]));
            endDateObj.setHours(0, 0, 0, 0);
            isInRange = end > start && end < endDateObj;
          } else {
            isInRange = false;
          }
        }
      }

      const handleClick = () => {
        if (isDisabled) return;

        if (isEndPicker) {
          setTempEndDate(dateStr);
          setShowEndPicker(false);
          onEndDateChange?.(dateStr);
        } else {
          setTempStartDate(dateStr);
          setShowStartPicker(false);
          onStartDateChange(dateStr);
          if (!singleDate) {
            // Clear end date when start date changes
            onEndDateChange?.('');
            setTempEndDate(dateStr);
            // Automatically open end picker
            setTimeout(() => setShowEndPicker(true), 100);
          }
        }
      };

      days.push(
      <button
        key={dateStr}
        type="button"
        onClick={handleClick}
        disabled={isDisabled}
        className={`
          p-2 text-sm rounded-md transition-colors
          ${
            isSelected
              ? 'text-white font-semibold'
              : isInRange
              ? 'font-medium'
              : 'hover:bg-gray-100'
          }
          ${
            isDisabled
              ? 'cursor-not-allowed'
              : 'cursor-pointer'
          }
          ${isToday && !isSelected ? 'font-bold' : ''}
        `}
        style={{
          backgroundColor: isSelected
            ? theme.colors.semantic.info
            : isStartDateInEndPicker
            ? '#dbeafe'
            : isInRange
            ? '#dbeafe'
            : isPast
            ? '#f3f4f6'
            : (disableToday && isToday)
            ? '#f3f4f6'
            : !isAvailable && availabilityMap
            ? '#fee2e2'
            : isToday
            ? '#dbeafe'
            : availabilityMap && isAvailable
            ? '#dcfce7'
            : 'transparent',
          color: isSelected
            ? 'white'
            : isStartDateInEndPicker
            ? '#1e40af'
            : isInRange
            ? '#1e40af'
            : isPast
            ? '#9ca3af'
            : isDisabled
            ? '#9ca3af'
            : theme.colors.text.primary,
        }}
      >
        {day}
      </button>
    );
  }

    return days;
  };

  const formatDateDisplay = (dateStr: string) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className={`${singleDate ? 'max-w-md' : 'grid md:grid-cols-2 gap-6'} ${className}`}>
      {/* Start Date */}
      <div className="relative" ref={startPickerRef}>
        <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.semantic.info }}>
          <CalendarIcon className="inline h-4 w-4 mr-1" />
          Dropoff Date
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowStartPicker(!showStartPicker)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-left"
            style={{ borderColor: theme.colors.secondary[300] }}
          >
            <span className={startDate ? 'text-gray-900' : 'text-gray-500'}>
              {startDate ? formatDateDisplay(startDate) : 'Select dropoff date'}
            </span>
          </button>
          {startDate && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onStartDateChange('');
                setTempStartDate('');
                if (!singleDate) {
                  onEndDateChange?.('');
                  setTempEndDate('');
                }
                onClearDates?.();
              }}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <XIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        {showStartPicker && (
          <div className="absolute z-50 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={() => {
                const dateStr = tempStartDate || getToday(disableToday);
                const parts = dateStr.split('-');
                const newDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                newDate.setDate(1); // Set to 1st of month before changing month
                newDate.setMonth(newDate.getMonth() - 1);
                const year = newDate.getFullYear();
                const month = String(newDate.getMonth() + 1).padStart(2, '0');
                const day = String(newDate.getDate()).padStart(2, '0');
                setTempStartDate(`${year}-${month}-${day}`);
              }}
                className="text-gray-600 hover:text-gray-800"
              >
                ←
              </button>
              <div className="font-medium">
                {(() => {
                  const dateStr = tempStartDate || getToday(disableToday);
                  const parts = dateStr.split('-');
                  const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                })()}
              </div>
              <button
                type="button"
                onClick={() => {
                const dateStr = tempStartDate || getToday(disableToday);
                const parts = dateStr.split('-');
                const newDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                newDate.setDate(1); // Set to 1st of month before changing month
                newDate.setMonth(newDate.getMonth() + 1);
                const year = newDate.getFullYear();
                const month = String(newDate.getMonth() + 1).padStart(2, '0');
                const day = String(newDate.getDate()).padStart(2, '0');
                setTempStartDate(`${year}-${month}-${day}`);
              }}
                className="text-gray-600 hover:text-gray-800"
              >
                →
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-xs text-gray-500 mb-2">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                <div key={day} className="text-center font-medium p-2">{day}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {(() => {
                const dateStr = tempStartDate || getToday(disableToday);
                const parts = dateStr.split('-');
                return generateCalendarDays(new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])));
              })()}
            </div>
          </div>
        )}
      </div>

      {/* End Date */}
      {!singleDate && (
      <div className="relative" ref={endPickerRef}>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <CalendarIcon className="inline h-4 w-4 mr-1" />
          Pickup Date
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => startDate && setShowEndPicker(!showEndPicker)}
            disabled={!startDate}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-left"
            style={{ borderColor: theme.colors.secondary[300] }}
          >
            <span className={endDate ? 'text-gray-900' : 'text-gray-500'}>
              {endDate ? formatDateDisplay(endDate) : startDate ? 'Select pickup date' : 'Select dropoff date first'}
            </span>
          </button>
          {endDate && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEndDateChange?.('');
                setTempEndDate('');
                onClearDates?.();
              }}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <XIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        {showEndPicker && startDate && (
          <div className="absolute z-50 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={() => {
                const dateStr = tempEndDate || startDate || getToday(disableToday);
                const parts = dateStr.split('-');
                const newDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                newDate.setDate(1); // Set to 1st of month before changing month
                newDate.setMonth(newDate.getMonth() - 1);
                const year = newDate.getFullYear();
                const month = String(newDate.getMonth() + 1).padStart(2, '0');
                const day = String(newDate.getDate()).padStart(2, '0');
                setTempEndDate(`${year}-${month}-${day}`);
              }}
                className="text-gray-600 hover:text-gray-800"
              >
                ←
              </button>
              <div className="font-medium">
                {(() => {
                  const dateStr = tempEndDate || startDate || getToday(disableToday);
                  const parts = dateStr.split('-');
                  const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                })()}
              </div>
              <button
                type="button"
                onClick={() => {
                const dateStr = tempEndDate || startDate || getToday(disableToday);
                const parts = dateStr.split('-');
                const newDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                newDate.setDate(1); // Set to 1st of month before changing month
                newDate.setMonth(newDate.getMonth() + 1);
                const year = newDate.getFullYear();
                const month = String(newDate.getMonth() + 1).padStart(2, '0');
                const day = String(newDate.getDate()).padStart(2, '0');
                setTempEndDate(`${year}-${month}-${day}`);
              }}
                className="text-gray-600 hover:text-gray-800"
              >
                →
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-xs text-gray-500 mb-2">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                <div key={day} className="text-center font-medium p-2">{day}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {(() => {
                const dateStr = tempEndDate || startDate || getToday(disableToday);
                const parts = dateStr.split('-');
                return generateCalendarDays(new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])), true);
              })()}
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default DateRangePicker;
